import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { COMPANY } from "@/lib/index";

const springGentle = { type: "spring" as const, stiffness: 300, damping: 35 };

const processSteps = [
  {
    num: "01",
    title: "Beratungsgespräch",
    desc: "Kostenloses Erstgespräch zur Analyse Ihrer Ziele, Zielgruppen und aktuellen Situation.",
    icon: "💬",
  },
  {
    num: "02",
    title: "Strategie & Konzept",
    desc: "Wir entwickeln eine maßgeschneiderte Strategie – KI-Design, Social Media oder beides.",
    icon: "🗺️",
  },
  {
    num: "03",
    title: "Umsetzung & Content",
    desc: "Professionelle Produktion aller Inhalte mit modernster KI-Technologie.",
    icon: "⚡",
  },
  {
    num: "04",
    title: "Optimierung & Wachstum",
    desc: "Laufende Analyse, Reporting und kontinuierliche Verbesserung aller Maßnahmen.",
    icon: "📈",
  },
];

const whyUsPoints = [
  "30+ Jahre europaweite Agenturerfahrung",
  "KI-gestützte Kreativprozesse auf neustem Stand",
  "Vollständige 360°-Kommunikationsbetreuung",
  "Transparentes Reporting & messbare KPIs",
  "Persönlicher Ansprechpartner – kein Ticket-System",
  "Flexibel für Unternehmen und Privathaushalte",
];

export default function AboutSection() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });

  return (
    <>
      {/* Process Section */}
      <section id="social-media" className="py-28 bg-card/50 border-y border-border" ref={ref}>
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={springGentle}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <span
              className="inline-block text-xs font-semibold uppercase tracking-[0.2em] mb-4 px-3 py-1 rounded-full border"
              style={{ color: "oklch(0.60 0.18 185)", borderColor: "oklch(0.60 0.18 185 / 0.3)", background: "oklch(0.60 0.18 185 / 0.08)" }}
            >
              Unser Prozess
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-5">
              So arbeiten <span style={{ color: "oklch(0.72 0.20 55)" }}>wir zusammen</span>
            </h2>
            <p className="text-muted-foreground text-lg">
              Strukturiert, transparent und ergebnisorientiert – von der ersten Idee bis zur laufenden Betreuung.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.map((step, i) => (
              <motion.div
                key={step.num}
                initial={{ opacity: 0, y: 40 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ ...springGentle, delay: 0.15 + i * 0.12 }}
                className="relative"
              >
                {/* Connector line */}
                {i < processSteps.length - 1 && (
                  <div
                    className="absolute top-8 left-full w-6 h-px hidden lg:block"
                    style={{ background: "linear-gradient(90deg, oklch(0.72 0.20 55 / 0.5), transparent)" }}
                  />
                )}
                <div
                  className="rounded-2xl bg-background border border-border p-6 h-full hover:border-primary/40 transition-colors"
                  style={{ boxShadow: "0 4px 20px -4px oklch(0.72 0.20 55 / 0.08)" }}
                >
                  <div className="flex items-start gap-3 mb-4">
                    <span
                      className="font-heading font-bold text-2xl"
                      style={{ color: "oklch(0.72 0.20 55 / 0.4)" }}
                    >
                      {step.num}
                    </span>
                    <span className="text-2xl">{step.icon}</span>
                  </div>
                  <h3 className="font-heading font-bold text-foreground text-base mb-2">{step.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{step.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="ueber-uns" className="py-28">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left: Text */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ ...springGentle, delay: 0.2 }}
            >
              <span
                className="inline-block text-xs font-semibold uppercase tracking-[0.2em] mb-5 px-3 py-1 rounded-full border"
                style={{ color: "oklch(0.72 0.20 55)", borderColor: "oklch(0.72 0.20 55 / 0.3)", background: "oklch(0.72 0.20 55 / 0.08)" }}
              >
                Über uns
              </span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">
                Ihre Vision,{" "}
                <span style={{ color: "oklch(0.72 0.20 55)" }}>unsere Leidenschaft</span>
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed mb-4">
                Aus <strong className="text-foreground">pixnetmedia</strong> wird KI Werbestudio. Mit über 30 Jahren europaweiter Erfahrung und einer beeindruckenden Referenzliste setzen wir Ihre Ideen mit modernster KI-Technologie um.
              </p>
              <p className="text-muted-foreground text-base leading-relaxed mb-4">
                Als erfahrener Social-Media-Spezialist übernehmen wir die gesamte digitale Kommunikation Ihres Unternehmens – von der Strategie über die Content-Erstellung bis zur Community-Betreuung.
              </p>
              <p className="text-muted-foreground text-base leading-relaxed mb-8">
                Mit fundiertem Fachwissen über Plattformen, Algorithmen und aktuelle Trends sorgen wir für eine professionelle, konsistente und erfolgreiche Markenpräsenz – ganz gleich ob für Firmen oder Privathaushalte.
              </p>

              <ul className="space-y-3">
                {whyUsPoints.map((point, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: -15 }}
                    animate={inView ? { opacity: 1, x: 0 } : {}}
                    transition={{ ...springGentle, delay: 0.35 + i * 0.07 }}
                    className="flex items-center gap-3 text-sm text-foreground/90"
                  >
                    <CheckCircle2 size={16} className="flex-shrink-0" style={{ color: "oklch(0.72 0.20 55)" }} />
                    {point}
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            {/* Right: Info Card */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ ...springGentle, delay: 0.3 }}
              className="relative"
            >
              <div
                className="rounded-3xl bg-card border border-border p-8 relative overflow-hidden"
                style={{ boxShadow: "0 20px 60px -15px oklch(0.72 0.20 55 / 0.2)" }}
              >
                {/* Background glow */}
                <div
                  className="absolute -top-20 -right-20 w-60 h-60 rounded-full opacity-10"
                  style={{ background: "radial-gradient(circle, oklch(0.72 0.20 55) 0%, transparent 70%)" }}
                />

                <div className="relative z-10">
                  {/* Profile */}
                  <div className="flex items-center gap-4 mb-8 pb-8 border-b border-border">
                    <div
                      className="w-16 h-16 rounded-2xl flex items-center justify-center text-2xl font-heading font-bold"
                      style={{ background: "oklch(0.72 0.20 55 / 0.15)", color: "oklch(0.72 0.20 55)" }}
                    >
                      MD
                    </div>
                    <div>
                      <div className="font-heading font-bold text-foreground text-lg">{COMPANY.founder}</div>
                      <div className="text-muted-foreground text-sm">Inhaber & KI-Spezialist</div>
                      <div className="text-muted-foreground text-xs mt-1">pixnetmedia · KI Werbestudio</div>
                    </div>
                  </div>

                  {/* Contact info */}
                  <div className="space-y-4">
                    {[
                      { label: "Standort", value: COMPANY.address, icon: "📍" },
                      { label: "Telefon", value: COMPANY.phone, icon: "📞", href: `tel:${COMPANY.phone}` },
                      { label: "E-Mail", value: COMPANY.email, icon: "✉️", href: `mailto:${COMPANY.email}` },
                      { label: "Website", value: COMPANY.website, icon: "🌐", href: `https://${COMPANY.website}` },
                      { label: "Öffnungszeiten", value: COMPANY.openingHours, icon: "🕐" },
                    ].map((item) => (
                      <div key={item.label} className="flex items-start gap-3">
                        <span className="text-base">{item.icon}</span>
                        <div>
                          <div className="text-xs text-muted-foreground uppercase tracking-wide mb-0.5">{item.label}</div>
                          {item.href ? (
                            <a
                              href={item.href}
                              target={item.href.startsWith("http") ? "_blank" : undefined}
                              rel="noopener noreferrer"
                              className="text-sm text-foreground hover:text-primary transition-colors"
                            >
                              {item.value}
                            </a>
                          ) : (
                            <div className="text-sm text-foreground">{item.value}</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
}
